import React, { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { I18n } from 'react-redux-i18n';

import 'dayjs/locale/pt-br';
import ReactTooltip from "react-tooltip";
import { useTheme } from "styled-components";
import Icon from "../elements/Icon";
import InfoIcon from '../../svg/ic_info_outline.svg';
import numberHelpers from "../../helpers/number";

const ExtractCard = ({ extract, balance, date, type = 'D', customDescription = "" }) => {
  const { colors } = useTheme();
  const transaction = extract;

  const types = useMemo(() => ({
    C: {
      title: "Entrada de dinheiro",
      icon: "arrow-forward-outline",
      color: "#59AC7C"
    },
    D: {
      title: "Saída de dinheiro",
      icon: "arrow-back-outline",
      color: "#BE00FF"
    },
    S: {
      title: "Saldo",
      icon: "briefcase-outline",
      color: "#BE00FF"
    },
    F: {
      title: date ? "Transação agendada" : "Transação aguardando validação de conta",
      icon: date ? "calendar-outline" : "alert-triangle-outline",
      color: date ? colors.brand.primary : colors.feedback.warning,
    }
  }), [date]);

  const handleDate = useCallback(() => {
    const isToday = dayjs(date).isSame(dayjs(), 'day');
    return isToday ? I18n.t('date.today') : dayjs(date).locale('pt-br').format("DD MMM").toLocaleUpperCase();
  }, [date]);

  const handleTitle = useCallback(() => {
    var title = types[type].title;
    var t = transaction;

    if (t && t.details && t.details.item) {
      title = t.details.item + " | Evento " + t.details.event + " | Pedido " + t.details.order + " | Lote(s) " + t.details.lots;
    }
    else if (t && typeof t.comment === 'string') {
      title = t.comment;
    }
    else if(t && t.description) {
      title = t.description;
    } 
     else if (t && t.counterpart && (t.historyCode === 1201 || t.historyCode === 1301 || t.historyCode === 1211)) {
      title = t.counterpart.name;
    }

    return title;
  }, [type, transaction]);

  const handleCustomDescription = useCallback(() => {
    var t = transaction;

    if (t && t.details && t.details.customDescription) {
      return t.details.customDescription;
    }
    if(t && t.description) {
      return t.description;
    }

    if (t && typeof t.comment === 'string') {
      return t.comment;
    }

    return '';
  }, [transaction]);

  const handleValue = useCallback(() => {
    var value = balance || 0;
    if (type === "D") value *= -1;
    return value.toLocaleString('pt-br', { style: 'currency', currency: "BRL" });
  }, [balance]);

  const renderGroupData = function (groupData) {
    if (!groupData || !(groupData instanceof Array)) return null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {groupData.map(function (data, index) {
          return (
            <span key={index} style={{ fontSize: '14px', color: 'rgba(115, 117, 125, 1)' }}>
              {data.desc + " | Lote " + data.lot}
            </span>
          );
        })}
      </div>
    );
  };


  const handleCounterpart = useCallback(() => {
    if (transaction && transaction.counterpart && transaction.counterpart.taxIdentifier && transaction.counterpart.taxIdentifier.taxId && transaction.counterpart.name) {
      return ' - ' +transaction.counterpart.name + " - " + numberHelpers.documentMask(transaction.counterpart.taxIdentifier.taxId);
    }
    return '';
  }, [transaction]);

  const renderGroupAmounts = function (groupData) {
    if (!groupData || !(groupData instanceof Array)) return null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {groupData.map(function (data, index) {
          return (
            <span key={index} style={{ fontSize: '14px', color: 'rgba(115, 117, 125, 1)', textAlign: 'right' }}>
              {data.amount.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="extract-card">
      <div className="extract-card" id="extract-card-desktop">
        <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '-15px'}}>
          <div className="extract-card__icon" title={types[type].title}>
              <Icon icon={types[type].icon} color={types[type].color} />
            </div>
          <p className="extract-card__content__date">
                  {date && handleDate()}
                  {handleCounterpart()}
            </p>
        </div>
        <div className="extract-card__content">
          <div>
            <h6 className="extract-card__content__title">
              <span>
                {type === 'F' && (
                  <InfoIcon
                    style={{ marginBottom: '2px', marginLeft: '18px' }}
                    data-tip="React-tooltip"
                  />
                )}
              </span>
            </h6>

            <ReactTooltip
              backgroundColor="#66696F"
              className="extract-card__tooltip"
              place="right"
              type="dark"
              effect="solid"
            >
              {date ? (
                <span>Transações agendadas após as 15h serão agendadas para o próximo dia útil</span>
              ) : (
                <span>
                  Essa transação será realizada após a validação dos dados bancários da conta de destino.
                  Isso acontecerá automaticamente, em até 24hrs. Caso a conta não seja validada essa transação será cancelada.
                </span>
              )}
            </ReactTooltip>
            <p className="extract-card__content__title">{handleTitle()}</p>

            {transaction && transaction.details && renderGroupData(transaction.details.groupData)}
            
          </div>
        <div>
          <p
            className="extract-card__content__value"
            style={{ color: type === 'C' ? '#59AC7C' : "rgba(102, 105, 111, 1)", marginBottom: '0px', fontSize: '16px', fontWeight: '600' }}
          >
            {handleValue()}
          </p>

          {transaction && transaction.details && renderGroupAmounts(transaction.details.groupData)}
        </div>
          
        </div>


      </div>

      <div className="extract-card" id="extract-card-mobile">
        <div className="extract-card__icon">
          <Icon icon={types[type].icon} color={types[type].color} />
        </div>
        <div className="extract-card__content" style={{ alignItems: 'center' }}>
          <h6 className="extract-card__content__title" style={{ fontSize: '12px' }}>{handleTitle()}</h6>
          <p
            className="extract-card__content__value"
            style={{ color: type === 'C' ? '#59AC7C' : "#66696F", fontSize: '12px', marginBottom: '0px' }}
          >
            {handleValue()}
          </p>
        </div>
        <p className="extract-card__content__date" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0px' }}>{date && handleDate()}</p>
      </div>
    </div>
  );
};

export default ExtractCard;
